import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/StatusBadge";
import NewTicketSheet from "@/components/NewTicketSheet";
import TicketDetailSheet from "@/components/TicketDetailSheet";
import SiteHotspotChart from "@/components/SiteHotspotChart";
import { Plus, TicketIcon, AlertTriangle, CheckCircle2, Activity, ShieldCheck, School, Archive, Download, FileText, ChevronUp, ChevronDown, ChevronsUpDown, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SITES, SITE_SHORT, siteTagClass, siteCrest, subscribeSiteCrests } from "@/lib/sites";

const DEPARTMENTS = ["IT", "Maintenance", "Custodial", "Mechanic"];

// Rank maps so Priority + Status sort by severity/lifecycle, not alphabet.
const PRIORITY_RANK = { Low: 1, Medium: 2, High: 3, Critical: 4 };
const STATUS_RANK = { Open: 1, "In Progress": 2, Resolved: 3, Closed: 4 };

const SortableHead = ({ label, sortKey, active, dir, onClick }) => {
  const isActive = active === sortKey;
  const Icon = isActive ? (dir === "asc" ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <TableHead
      onClick={() => onClick(sortKey)}
      data-testid={`sort-${sortKey}`}
      aria-sort={isActive ? (dir === "asc" ? "ascending" : "descending") : "none"}
      className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold cursor-pointer select-none hover:text-zinc-900 transition-colors"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`h-3.5 w-3.5 ${isActive ? "text-zinc-900" : "text-zinc-400"}`} strokeWidth={2.25} />
      </span>
    </TableHead>
  );
};

const MetricCard = ({ label, value, icon: Icon, tone }) => (
  <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid={`metric-${label.replace(/\s+/g,'-').toLowerCase()}`}>
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-[0.1em] text-zinc-500">{label}</div>
      <Icon className={`h-4 w-4 ${tone}`} />
    </div>
    <div className="mt-4 font-heading text-4xl font-light tracking-tighter text-zinc-950">{value ?? 0}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();

  const isAdmin = user?.role === "admin";
  const userSites = user?.sites || (user?.site ? [user.site] : []);
  const isSiteMaster = isAdmin || userSites.includes("District");
  const isDeptMaster = isAdmin || user?.role === "it" || user?.role === "principal" || user?.role === "secretary";
  const allowedSiteCodes = isSiteMaster ? SITES.map((s) => s.code) : userSites;
  const canFilterSite = isSiteMaster || userSites.length > 1;

  const [tickets, setTickets] = useState([]);
  const [archiveYears, setArchiveYears] = useState([]);
  const [archiveYear, setArchiveYear] = useState("all");
  const [stats, setStats] = useState(null);
  const [dept, setDept] = useState(isDeptMaster ? "all" : (user?.role === "custodian" ? "Custodial" : user?.role === "mechanic" ? "Mechanic" : "all"));
  const [site, setSite] = useState(isSiteMaster ? "all" : (userSites.length === 1 ? userSites[0] : "all"));
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [archivedView, setArchivedView] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc"); // newest first by default

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "created" ? "desc" : "asc"); // A-Z for text, newest first for date
    }
  };

  const sortedTickets = useMemo(() => {
    const arr = [...tickets];
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (t) => {
      switch (sortKey) {
        case "title":     return (t.title || "").toLowerCase();
        case "site":      return (SITE_SHORT[t.site] || t.site || "").toLowerCase();
        case "location":  return (t.location || "").toLowerCase();
        case "requester": return (t.requester_name || t.requester_email || "").toLowerCase();
        case "dept":      return (t.department || "").toLowerCase();
        case "priority":  return PRIORITY_RANK[t.priority] || 0;
        case "status":    return STATUS_RANK[t.status] || 0;
        case "assignee":  return (t.assignee?.name || "").toLowerCase();
        case "created":   return new Date(t.created_at).getTime() || 0;
        default:          return "";
      }
    };
    arr.sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return arr;
  }, [tickets, sortKey, sortDir]);

  // Split the queue into Active vs. Closed/Resolved so the main table only shows
  // work that still needs attention. Closed & Resolved live in a collapsible bar below.
  const CLOSED_STATUSES = ["Resolved", "Closed"];
  // Free-text search across title, ticket #, requester, location, category
  const [searchQuery, setSearchQuery] = useState("");
  const searchedSorted = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedTickets;
    return sortedTickets.filter((t) => {
      const hay = [
        t.title,
        t.id,
        t.id ? t.id.slice(0, 8) : "",
        t.category,
        t.requester_name,
        t.requester_email,
        t.location,
        t.location_type,
        t.assignee?.name,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [sortedTickets, searchQuery]);
  const activeSorted = useMemo(
    () => searchedSorted.filter((t) => !CLOSED_STATUSES.includes(t.status)),
    [searchedSorted]
  );
  const closedResolvedSorted = useMemo(
    () => searchedSorted.filter((t) => CLOSED_STATUSES.includes(t.status)),
    [searchedSorted]
  );
  const resolvedCount = useMemo(
    () => closedResolvedSorted.filter((t) => t.status === "Resolved").length,
    [closedResolvedSorted]
  );
  const closedCount = closedResolvedSorted.length - resolvedCount;
  const [showClosed, setShowClosed] = useState(false);
  const [closedSubTab, setClosedSubTab] = useState("all"); // 'all' | 'Resolved' | 'Closed'
  // Active (Open + In Progress) collapsible section — defaults to expanded so users
  // always land on the actionable queue.
  const [showActive, setShowActive] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("all"); // 'all' | 'Open' | 'In Progress'
  const openCount = useMemo(
    () => activeSorted.filter((t) => t.status === "Open").length,
    [activeSorted]
  );
  const inProgressCount = activeSorted.length - openCount;
  const activeFiltered = useMemo(() => {
    if (activeSubTab === "all") return activeSorted;
    return activeSorted.filter((t) => t.status === activeSubTab);
  }, [activeSorted, activeSubTab]);
  // When the Status filter is set to Resolved/Closed → auto-expand Closed section.
  // When set to Open/In Progress → auto-focus the matching Active sub-tab.
  // When cleared back to "all" → reset BOTH sub-tabs to "all" so filters don't stay stuck.
  useEffect(() => {
    if (status === "Resolved" || status === "Closed") {
      setShowClosed(true);
      setClosedSubTab(status);
    } else if (status === "Open" || status === "In Progress") {
      setShowActive(true);
      setActiveSubTab(status);
    } else if (status === "all") {
      setActiveSubTab("all");
      setClosedSubTab("all");
    }
  }, [status]);
  const closedFiltered = useMemo(() => {
    if (closedSubTab === "all") return closedResolvedSorted;
    return closedResolvedSorted.filter((t) => t.status === closedSubTab);
  }, [closedResolvedSorted, closedSubTab]);

  // Render text with any occurrence of the current search query wrapped in <mark>
  // so users can spot the match inside a title at a glance.
  const highlightMatch = (text) => {
    const q = searchQuery.trim();
    if (!q || !text) return text;
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = String(text).split(new RegExp(`(${escaped})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 text-zinc-950 rounded px-0.5" data-testid="search-match">{part}</mark>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  const load = useCallback(async () => {
    try {
      if (isAdmin && archivedView) {
        const params = { department: dept, status, priority, site };
        if (archiveYear && archiveYear !== "all") params.year = archiveYear;
        const [a, s] = await Promise.all([
          api.get("/archive/tickets", { params }),
          api.get("/tickets/stats"),
        ]);
        setTickets(a.data?.tickets || []);
        setArchiveYears(a.data?.years || []);
        setStats(s.data);
      } else {
        const params = { department: dept, status, priority, site };
        const [t, s] = await Promise.all([
          api.get("/tickets", { params }),
          api.get("/tickets/stats"),
        ]);
        setTickets(t.data);
        setStats(s.data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [dept, status, priority, site, isAdmin, archivedView, archiveYear]);

  useEffect(() => { load(); }, [load]);

  // Re-render when admin uploads/removes a crest so the pills refresh live.
  const [, setCrestTick] = useState(0);
  useEffect(() => subscribeSiteCrests(() => setCrestTick((n) => n + 1)), []);

  const scopeText = useMemo(() => {
    if (isAdmin) return "Admin — full district access.";
    const siteLabel = isSiteMaster
      ? "district-wide"
      : userSites.length > 1
      ? `${userSites.length} sites`
      : (SITE_SHORT[userSites[0]] || userSites[0] || "—");
    if (user?.role === "principal" || user?.role === "secretary" || user?.role === "it")
      return `Scope: ${siteLabel} · all departments (${user.role}).`;
    if (user?.role === "maintenance") return `Scope: ${siteLabel} · Maintenance + Custodial (read-only).`;
    if (user?.role === "custodian")   return `Scope: ${siteLabel} · Custodial only.`;
    if (user?.role === "mechanic")    return `Scope: ${siteLabel} · Mechanic only.`;
    return `Scope: ${siteLabel}.`;
  }, [isAdmin, isSiteMaster, user, userSites]);

  return (
    <div className="p-4 md:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">Operations · ESUSD</div>
            {isSiteMaster && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-zinc-950 text-zinc-50 px-2 py-0.5 rounded-full" data-testid="master-badge">
                <ShieldCheck className="h-3 w-3" strokeWidth={2.4} /> District
              </span>
            )}
            {isDeptMaster && !isAdmin && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-[#0055FF] text-white px-2 py-0.5 rounded-full">
                Dept-master
              </span>
            )}
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mt-1">Ticket Queue</h1>
          <p className="text-sm text-zinc-500 mt-1">{scopeText}</p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap w-full md:w-auto">
          {isAdmin && (
            <>
              <Button
                variant={archivedView ? "default" : "outline"}
                onClick={() => setArchivedView((v) => !v)}
                data-testid="archived-view-toggle"
                className={`h-11 rounded-md transition-colors duration-200 ${archivedView ? "bg-zinc-900 text-white hover:bg-zinc-800" : "border-zinc-300 text-zinc-800 hover:bg-zinc-50"}`}
              >
                <Archive className="h-4 w-4 mr-2" />
                {archivedView ? "Viewing archive" : "Show archive"}
              </Button>
              {archivedView && archiveYears.length > 0 && (
                <Select value={archiveYear} onValueChange={setArchiveYear}>
                  <SelectTrigger className="w-32 h-11" data-testid="archive-year-dropdown">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All years</SelectItem>
                    {archiveYears.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              {archivedView && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const params = { department: dept, status, priority, site };
                      if (archiveYear && archiveYear !== "all") params.year = archiveYear;
                      const r = await api.get("/archive/tickets.csv", { params, responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = archiveYear && archiveYear !== "all" ? `archive_${archiveYear}.csv` : "archive_all_years.csv";
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Archive CSV downloaded");
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  data-testid="archive-export-csv"
                  className="h-11 rounded-md border-zinc-300 text-zinc-800 hover:bg-zinc-50"
                >
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
              )}
              {archivedView && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const params = { department: dept, site };
                      if (archiveYear && archiveYear !== "all") params.year = archiveYear;
                      const r = await api.get("/archive/audit-pack.pdf", { params, responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = archiveYear && archiveYear !== "all" ? `audit_pack_${archiveYear}.pdf` : "audit_pack_all_years.pdf";
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Audit pack PDF downloaded");
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  data-testid="archive-audit-pack"
                  className="h-11 rounded-md border-zinc-300 text-zinc-800 hover:bg-zinc-50"
                >
                  <FileText className="h-4 w-4 mr-2" /> Audit pack
                </Button>
              )}
            </>
          )}
          {isSiteMaster && (
            <div className="flex items-center gap-2">
              <School className="h-4 w-4 text-zinc-500" />
              <Select value={site} onValueChange={setSite}>
                <SelectTrigger className="w-56 h-11" data-testid="master-site-dropdown">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sites</SelectItem>
                  {SITES.map((s) => (<SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            onClick={() => setOpenNew(true)}
            data-testid="dashboard-new-ticket-button"
            className="bg-[#0055FF] hover:bg-[#0040CC] text-white h-11 rounded-md transition-colors duration-200"
          >
            <Plus className="h-4 w-4 mr-2" /> New ticket
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <MetricCard label="Total" value={stats?.total} icon={TicketIcon} tone="text-zinc-500" />
        <MetricCard label="Open" value={stats?.open} icon={Activity} tone="text-indigo-500" />
        <MetricCard label="Critical" value={stats?.critical} icon={AlertTriangle} tone="text-red-500" />
        <MetricCard label="Closed / Resolved" value={stats?.closed_resolved} icon={CheckCircle2} tone="text-emerald-500" />
      </div>

      {/* Site hotspots — only for site-masters (District users + admin) */}
      {isSiteMaster && (
        <div className="mt-6">
          <SiteHotspotChart
            openBySite={stats?.open_by_site || {}}
            openBySiteDept={stats?.open_by_site_dept || {}}
            closedBySite={stats?.closed_by_site || {}}
            closedBySiteDept={stats?.closed_by_site_dept || {}}
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-md mt-6 p-4 flex flex-wrap items-center gap-3 justify-between">
        {isDeptMaster ? (
          <Tabs value={dept} onValueChange={setDept} className="w-full sm:w-auto max-w-full overflow-x-auto -mx-1 px-1">
            <TabsList data-testid="dept-tabs" className="min-w-max">
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              {DEPARTMENTS.map((d) => (
                <TabsTrigger key={d} value={d} data-testid={`tab-${d.toLowerCase()}`}>{d}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : user?.role === "maintenance" ? (
          // Maintenance has one-way read access to Custodial — offer a mini tab set
          // so they can pivot between their own team, Custodial requests, or both.
          <Tabs value={dept} onValueChange={setDept} className="w-full sm:w-auto max-w-full overflow-x-auto -mx-1 px-1">
            <TabsList data-testid="dept-tabs-maint" className="min-w-max">
              <TabsTrigger value="all" data-testid="tab-all">Both</TabsTrigger>
              <TabsTrigger value="Maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
              <TabsTrigger value="Custodial" data-testid="tab-custodial">Custodial</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : (
          <div className="flex items-center gap-2 text-sm text-zinc-700" data-testid="dept-lock-indicator">
            <span className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Department</span>
            <Badge kind="dept" value={user?.department} />
            <span className="text-xs text-zinc-400">Scoped to your team</span>
          </div>
        )}
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
          {/* Search — full width on mobile, ~200px on desktop */}
          <div className="relative w-full sm:w-52 order-first sm:order-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" strokeWidth={2} />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tickets…"
              data-testid="filter-search"
              aria-label="Search tickets"
              className="w-full h-10 pl-9 pr-8 rounded-md border border-zinc-200 bg-white text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                data-testid="filter-search-clear"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="flex-1 sm:w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {["Open","In Progress","Resolved","Closed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="flex-1 sm:w-40" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {["Low","Medium","High","Critical"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Open & In Progress — collapsible section (default: expanded so users always see actionable work) */}
      <div className="mt-6" data-testid="active-section">
        <button
          type="button"
          onClick={() => setShowActive((v) => !v)}
          aria-expanded={showActive}
          data-testid="active-toggle"
          className="w-full flex items-center gap-3 bg-white border border-zinc-200 rounded-md px-4 py-3 text-left hover:border-zinc-400 active:bg-zinc-50 transition-colors"
        >
          <Activity className="h-5 w-5 text-blue-600 shrink-0" strokeWidth={2} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-950">
              Open &amp; In Progress <span className="text-zinc-500 font-normal">· {activeSorted.length}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-0.5 truncate">
              {openCount} open · {inProgressCount} in progress — tap to {showActive ? "hide" : "view"}
            </div>
          </div>
          {showActive ? <ChevronUp className="h-5 w-5 text-zinc-400 shrink-0" /> : <ChevronDown className="h-5 w-5 text-zinc-400 shrink-0" />}
        </button>

        {showActive && (
          <div className="mt-3 space-y-3" data-testid="active-panel">
            {/* Sub-tabs: All / Open / In Progress */}
            <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full sm:w-auto max-w-full overflow-x-auto -mx-1 px-1">
              <TabsList data-testid="active-subtabs" className="min-w-max">
                <TabsTrigger value="all" data-testid="active-tab-all">All ({activeSorted.length})</TabsTrigger>
                <TabsTrigger value="Open" data-testid="active-tab-open">Open ({openCount})</TabsTrigger>
                <TabsTrigger value="In Progress" data-testid="active-tab-in-progress">In Progress ({inProgressCount})</TabsTrigger>
              </TabsList>
            </Tabs>

      {/* Table — desktop/tablet */}
      <div className="hidden md:block bg-white border border-zinc-200 rounded-md floating-x-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-zinc-200">
              <SortableHead label="Ticket"    sortKey="title"     active={sortKey} dir={sortDir} onClick={onSort} />
              <SortableHead label="Site"      sortKey="site"      active={sortKey} dir={sortDir} onClick={onSort} />
              <SortableHead label="Location"  sortKey="location"  active={sortKey} dir={sortDir} onClick={onSort} />
              <SortableHead label="Requester" sortKey="requester" active={sortKey} dir={sortDir} onClick={onSort} />
              <SortableHead label="Dept"      sortKey="dept"      active={sortKey} dir={sortDir} onClick={onSort} />
              <SortableHead label="Priority"  sortKey="priority"  active={sortKey} dir={sortDir} onClick={onSort} />
              <SortableHead label="Status"    sortKey="status"    active={sortKey} dir={sortDir} onClick={onSort} />
              <SortableHead label="Assignee"  sortKey="assignee"  active={sortKey} dir={sortDir} onClick={onSort} />
              <SortableHead label="Created"   sortKey="created"   active={sortKey} dir={sortDir} onClick={onSort} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeFiltered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-sm text-zinc-500 py-16">
                  No {activeSubTab === "all" ? "active" : activeSubTab.toLowerCase()} tickets match your filters.
                </TableCell>
              </TableRow>
            )}
            {activeFiltered.map((t) => (
              <TableRow
                key={t.id}
                onClick={() => setDetailId(t.id)}
                className="row-hover cursor-pointer hover:bg-zinc-50 border-b border-zinc-100"
                data-testid={`ticket-row-${t.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="text-sm font-medium text-zinc-950">{highlightMatch(t.title)}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-0.5">#{t.id.slice(0,8)} · {t.category}</div>
                    </div>
                    {(archivedView || t.archived_year) && (
                      <span className="text-[9px] uppercase tracking-[0.1em] bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1" data-testid="archived-badge">
                        <Archive className="h-2.5 w-2.5" /> {t.archived_year || "Archived"}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    {siteCrest(t.site) && (
                      <img
                        src={siteCrest(t.site)}
                        alt=""
                        className="h-5 w-5 rounded-sm object-contain shrink-0"
                        data-testid={`site-crest-${t.site}`}
                      />
                    )}
                    <span
                      className={`inline-flex items-center shrink-0 text-xs px-2 py-0.5 rounded-full font-mono border whitespace-nowrap ${siteTagClass(t.site)}`}
                      data-testid={`site-tag-${t.site}`}
                    >
                      {SITE_SHORT[t.site] || t.site || "—"}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {t.location ? (
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.05em] text-zinc-500">{t.location_type || "—"}</div>
                      <div className="text-sm text-zinc-800 truncate max-w-[180px]" title={t.location}>{t.location}</div>
                    </div>
                  ) : (
                    <span className="text-zinc-400 text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm text-zinc-800">{t.requester_name}</div>
                  <div className="text-xs text-zinc-500">{t.requester_email}</div>
                </TableCell>
                <TableCell><Badge kind="dept" value={t.department} /></TableCell>
                <TableCell><Badge kind="priority" value={t.priority} /></TableCell>
                <TableCell><Badge kind="status" value={t.status} /></TableCell>
                <TableCell className="text-sm text-zinc-700">{t.assignee?.name || <span className="text-zinc-400">—</span>}</TableCell>
                <TableCell className="text-xs text-zinc-500">{new Date(t.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Card list — mobile only (<md) */}
      <div className="md:hidden space-y-3" data-testid="dashboard-mobile-cards">
        {activeFiltered.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-md p-8 text-center text-sm text-zinc-500">
            No {activeSubTab === "all" ? "active" : activeSubTab.toLowerCase()} tickets match your filters.
          </div>
        )}
        {activeFiltered.map((t) => (
          <button
            key={t.id}
            onClick={() => setDetailId(t.id)}
            data-testid={`ticket-card-mobile-${t.id}`}
            className="w-full text-left bg-white border border-zinc-200 rounded-md p-4 hover:border-zinc-400 active:bg-zinc-50 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-mono text-zinc-500">#{t.id.slice(0,8)} · {t.category}</div>
                <div className="font-medium text-sm text-zinc-950 mt-0.5 line-clamp-2">{highlightMatch(t.title)}</div>
              </div>
              <Badge kind="status" value={t.status} />
            </div>
            <div className="mt-3 flex items-center flex-wrap gap-1.5">
              <div className="inline-flex items-center gap-1">
                {siteCrest(t.site) && (
                  <img src={siteCrest(t.site)} alt="" className="h-4 w-4 rounded-sm object-contain" />
                )}
                <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-mono border ${siteTagClass(t.site)}`}>
                  {SITE_SHORT[t.site] || t.site || "—"}
                </span>
              </div>
              <Badge kind="dept" value={t.department} />
              <Badge kind="priority" value={t.priority} />
              {(archivedView || t.archived_year) && (
                <span className="text-[9px] uppercase tracking-[0.1em] bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                  <Archive className="h-2.5 w-2.5" /> {t.archived_year || "Archived"}
                </span>
              )}
            </div>
            {t.location && (
              <div className="mt-2 text-xs text-zinc-600 truncate">
                <span className="text-zinc-400">{t.location_type || "Location"} · </span>{t.location}
              </div>
            )}
            <div className="mt-1 flex items-center justify-between gap-2 text-xs text-zinc-500">
              <span className="truncate">{t.requester_name} → {t.assignee?.name || <span className="italic text-zinc-400">Unassigned</span>}</span>
              <span className="shrink-0">{new Date(t.created_at).toLocaleDateString()}</span>
            </div>
          </button>
        ))}
      </div>
          </div>
        )}
      </div>

      {/* Closed & Resolved — collapsible section (default: hidden to keep the queue view clean) */}
      <div className="mt-6" data-testid="closed-resolved-section">
        <button
          type="button"
          onClick={() => setShowClosed((v) => !v)}
          aria-expanded={showClosed}
          data-testid="closed-resolved-toggle"
          className="w-full flex items-center gap-3 bg-white border border-zinc-200 rounded-md px-4 py-3 text-left hover:border-zinc-400 active:bg-zinc-50 transition-colors"
        >
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" strokeWidth={2} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-950">
              Closed &amp; Resolved <span className="text-zinc-500 font-normal">· {closedResolvedSorted.length}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-0.5 truncate">
              {resolvedCount} resolved · {closedCount} closed — tap to {showClosed ? "hide" : "view"}
            </div>
          </div>
          {showClosed ? <ChevronUp className="h-5 w-5 text-zinc-400 shrink-0" /> : <ChevronDown className="h-5 w-5 text-zinc-400 shrink-0" />}
        </button>

        {showClosed && (
          <div className="mt-3 space-y-3" data-testid="closed-resolved-panel">
            {/* Sub-tabs: All / Resolved / Closed */}
            <Tabs value={closedSubTab} onValueChange={setClosedSubTab} className="w-full sm:w-auto max-w-full overflow-x-auto -mx-1 px-1">
              <TabsList data-testid="closed-subtabs" className="min-w-max">
                <TabsTrigger value="all" data-testid="closed-tab-all">All ({closedResolvedSorted.length})</TabsTrigger>
                <TabsTrigger value="Resolved" data-testid="closed-tab-resolved">Resolved ({resolvedCount})</TabsTrigger>
                <TabsTrigger value="Closed" data-testid="closed-tab-closed">Closed ({closedCount})</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Desktop table */}
            <div className="hidden md:block bg-white border border-zinc-200 rounded-md floating-x-scroll">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-zinc-200">
                    <SortableHead label="Ticket"    sortKey="title"     active={sortKey} dir={sortDir} onClick={onSort} />
                    <SortableHead label="Site"      sortKey="site"      active={sortKey} dir={sortDir} onClick={onSort} />
                    <SortableHead label="Location"  sortKey="location"  active={sortKey} dir={sortDir} onClick={onSort} />
                    <SortableHead label="Requester" sortKey="requester" active={sortKey} dir={sortDir} onClick={onSort} />
                    <SortableHead label="Dept"      sortKey="dept"      active={sortKey} dir={sortDir} onClick={onSort} />
                    <SortableHead label="Priority"  sortKey="priority"  active={sortKey} dir={sortDir} onClick={onSort} />
                    <SortableHead label="Status"    sortKey="status"    active={sortKey} dir={sortDir} onClick={onSort} />
                    <SortableHead label="Assignee"  sortKey="assignee"  active={sortKey} dir={sortDir} onClick={onSort} />
                    <SortableHead label="Created"   sortKey="created"   active={sortKey} dir={sortDir} onClick={onSort} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {closedFiltered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-sm text-zinc-500 py-12">
                        No {closedSubTab === "all" ? "closed or resolved" : closedSubTab.toLowerCase()} tickets match your filters.
                      </TableCell>
                    </TableRow>
                  )}
                  {closedFiltered.map((t) => (
                    <TableRow
                      key={t.id}
                      onClick={() => setDetailId(t.id)}
                      className="row-hover cursor-pointer hover:bg-zinc-50 border-b border-zinc-100"
                      data-testid={`closed-ticket-row-${t.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm font-medium text-zinc-950">{highlightMatch(t.title)}</div>
                            <div className="text-xs text-zinc-500 font-mono mt-0.5">#{t.id.slice(0,8)} · {t.category}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {siteCrest(t.site) && (
                            <img src={siteCrest(t.site)} alt="" className="h-5 w-5 rounded-sm object-contain shrink-0" />
                          )}
                          <span className={`inline-flex items-center shrink-0 text-xs px-2 py-0.5 rounded-full font-mono border whitespace-nowrap ${siteTagClass(t.site)}`}>
                            {SITE_SHORT[t.site] || t.site || "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {t.location ? (
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.05em] text-zinc-500">{t.location_type || "—"}</div>
                            <div className="text-sm text-zinc-800 truncate max-w-[180px]" title={t.location}>{t.location}</div>
                          </div>
                        ) : (
                          <span className="text-zinc-400 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-zinc-800">{t.requester_name}</div>
                        <div className="text-xs text-zinc-500">{t.requester_email}</div>
                      </TableCell>
                      <TableCell><Badge kind="dept" value={t.department} /></TableCell>
                      <TableCell><Badge kind="priority" value={t.priority} /></TableCell>
                      <TableCell><Badge kind="status" value={t.status} /></TableCell>
                      <TableCell className="text-sm text-zinc-700">{t.assignee?.name || <span className="text-zinc-400">—</span>}</TableCell>
                      <TableCell className="text-xs text-zinc-500">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-3">
              {closedFiltered.length === 0 && (
                <div className="bg-white border border-zinc-200 rounded-md p-6 text-center text-sm text-zinc-500">
                  No {closedSubTab === "all" ? "closed or resolved" : closedSubTab.toLowerCase()} tickets.
                </div>
              )}
              {closedFiltered.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setDetailId(t.id)}
                  data-testid={`closed-ticket-card-mobile-${t.id}`}
                  className="w-full text-left bg-white border border-zinc-200 rounded-md p-4 hover:border-zinc-400 active:bg-zinc-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-mono text-zinc-500">#{t.id.slice(0,8)} · {t.category}</div>
                      <div className="font-medium text-sm text-zinc-950 mt-0.5 line-clamp-2">{highlightMatch(t.title)}</div>
                    </div>
                    <Badge kind="status" value={t.status} />
                  </div>
                  <div className="mt-3 flex items-center flex-wrap gap-1.5">
                    <div className="inline-flex items-center gap-1">
                      {siteCrest(t.site) && (
                        <img src={siteCrest(t.site)} alt="" className="h-4 w-4 rounded-sm object-contain" />
                      )}
                      <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-mono border ${siteTagClass(t.site)}`}>
                        {SITE_SHORT[t.site] || t.site || "—"}
                      </span>
                    </div>
                    <Badge kind="dept" value={t.department} />
                    <Badge kind="priority" value={t.priority} />
                  </div>
                  {t.location && (
                    <div className="mt-2 text-xs text-zinc-600 truncate">
                      <span className="text-zinc-400">{t.location_type || "Location"} · </span>{t.location}
                    </div>
                  )}
                  <div className="mt-1 flex items-center justify-between gap-2 text-xs text-zinc-500">
                    <span className="truncate">{t.requester_name} → {t.assignee?.name || <span className="italic text-zinc-400">Unassigned</span>}</span>
                    <span className="shrink-0">{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <NewTicketSheet open={openNew} onOpenChange={setOpenNew} onCreated={load} />
      <TicketDetailSheet ticketId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} onChanged={load} />
    </div>
  );
}
